import { Modal, Box, Typography, Button } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import CommonText from '../../../form-components/CommonText';

// ============================================
// Styled Components
// ============================================
const ModalContainer = styled(Box, {
  shouldForwardProp: prop => prop !== 'width',
})(({ width }) => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width,
  maxWidth: 'calc(100vw - 32px)',
  backgroundColor: '#fff',
  boxShadow: '0px 11px 15px -7px rgba(0,0,0,0.2)',
  borderRadius: '16px',
  padding: '32px',
  textAlign: 'center',
}));

const MessageText = styled(Typography)({
  fontSize: '18px',
  fontWeight: 500,
  color: '#333',
  marginBottom: '24px',
  whiteSpace: 'pre-line',
});

const HighlightText = styled(Typography, {
  shouldForwardProp: prop => prop !== 'highlightColor',
})(({ highlightColor }) => ({
  color: highlightColor,
  fontWeight: 600,
  fontSize: '20px',
}));

const ButtonContainer = styled(Box)({
  display: 'flex',
  gap: '12px',
});

const CancelButton = styled(Button)({
  flex: 1,
  height: '48px',
  borderRadius: '8px',
  border: '1px solid #E0E0E0',
  backgroundColor: '#fff',
  fontSize: '20px',
  fontWeight: 500,
  color: '#333',
  '&:hover': {
    backgroundColor: '#F5F5F5',
  },
});

const ConfirmButton = styled(Button, {
  shouldForwardProp: prop => prop !== 'confirmColor',
})(({ confirmColor }) => ({
  flex: 1,
  height: '48px',
  borderRadius: '8px',
  backgroundColor: confirmColor,
  fontSize: '20px',
  fontWeight: 500,
  color: '#fff',
  '&:hover': {
    backgroundColor: confirmColor,
    opacity: 0.9,
  },
}));

// ============================================
// Main Component
// ============================================
/**
 * 확인 모달 컴포넌트
 * @param {boolean} open - 모달 열림 상태
 * @param {function} onClose - 모달 닫기 핸들러
 * @param {function} onConfirm - 확인 버튼 핸들러
 * @param {string} message - 표시할 메시지 (기본값: "정말로 종료하시겠습니까?")
 * @param {string} highlightText - 강조할 텍스트 (기본값: "종료")
 * @param {string} highlightColor - 강조 텍스트 색상 (기본값: "#F44336")
 * @param {string} cancelText - 취소 버튼 텍스트 (기본값: "닫기")
 * @param {string} confirmText - 확인 버튼 텍스트 (기본값: "종료")
 * @param {string} confirmColor - 확인 버튼 색상 (기본값: "#F44336")
 */
const ConfirmModal = ({
  open,
  onClose,
  onConfirm,
  message,
  highlightText = '종료',
  highlightColor = '#F44336',
  cancelText = '닫기',
  confirmText = '종료',
  confirmColor = '#F44336',
  /** 문장만으로 부족할 때 아래에 붙이는 내용(변경 전후 표 등). 없으면 기존과 같다. */
  detail = null,
  /** 모달 너비. 긴 문장을 줄 단위로 보여줘야 할 때만 넓힌다. */
  width = '400px',
}) => {
  const { t } = useTranslation();

  const renderMessage = () => {
    if (message) {
      const parts = message.split(highlightText);
      if (parts.length > 1) {
        return (
          <>
            {parts[0]}
            <HighlightText component="span" highlightColor={highlightColor}>
              {highlightText}
            </HighlightText>
            {parts[1]}
          </>
        );
      }
      return message;
    }

    return (
      <>
        <CommonText component="span">정말로 </CommonText>
        <HighlightText component="span" highlightColor={highlightColor}>
          {t(highlightText)}
        </HighlightText>
        <CommonText component="span">하시겠습니까?</CommonText>
      </>
    );
  };

  return (
    <Modal open={open} onClose={onClose}>
      <ModalContainer width={width}>
        <MessageText>{renderMessage()}</MessageText>
        {detail}
        <ButtonContainer>
          <CancelButton onClick={onClose}>{t(cancelText)}</CancelButton>
          <ConfirmButton onClick={onConfirm} confirmColor={confirmColor}>
            {t(confirmText)}
          </ConfirmButton>
        </ButtonContainer>
      </ModalContainer>
    </Modal>
  );
};

export default ConfirmModal;
